type AnyEvent = { type: string };
type Handler<E> = (event: E) => void;

/**
 * Minimal strongly-typed event bus. The simulation emits events; rendering,
 * UI and audio subscribe. This keeps game logic free of DOM/audio concerns.
 */
export class Emitter<E extends AnyEvent> {
  private readonly handlers = new Map<E['type'], Set<Handler<E>>>();
  private readonly anyHandlers = new Set<Handler<E>>();

  on<K extends E['type']>(type: K, handler: Handler<Extract<E, { type: K }>>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const h = handler as Handler<E>;
    set.add(h);
    return () => set.delete(h);
  }

  onAny(handler: Handler<E>): () => void {
    this.anyHandlers.add(handler);
    return () => this.anyHandlers.delete(handler);
  }

  emit(event: E): void {
    const set = this.handlers.get(event.type as E['type']);
    if (set) for (const h of [...set]) h(event);
    for (const h of [...this.anyHandlers]) h(event);
  }

  clear(): void {
    this.handlers.clear();
    this.anyHandlers.clear();
  }
}
